import { ColumnDef } from '@tanstack/react-table';
import AdvanceTable from 'components/base/AdvanceTable';
import { currencyFormat } from 'helpers/utils';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import { Link } from 'react-router-dom';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// Define the OrderItem type based on the GraphQL response
interface ConfigurableAttribute {
  content: string;
  label: string;
}

interface OrderItem {
  item_id: number;
  product_id: number;
  name: string;
  sku: string;
  price: number;
  total_price: number;
  comission: number;
  actual_seller_amount: number;
  qty_commanded: number;
  qty_invoiced: number;
  qty_shipped: number;
  qty_refunded: number;
  TVA: number;
  image: string;
  is_variant: boolean;
  seller_reference?: string;
  configurable_attributes?: ConfigurableAttribute[];
}

// Format status label for display by converting underscores to spaces and capitalizing each word
const formatStatusLabel = (label: string): string => {
  if (!label) return 'Unknown';
  
  // Replace underscores with spaces and capitalize each word
  return label
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// Helper function to determine badge type and label based on product status
const getItemStatusConfig = (item: OrderItem, t: any): { type: string; icon: string; label: string; code: string } => {
  // Default status if item is undefined
  if (!item) return { type: 'secondary', icon: 'help-circle', label: t('orderStatus.pending'), code: 'pending' };
  
  // Determine status code based on quantities
  let statusCode = 'pending';
  
  // Check for refunds first
  if (item.qty_refunded && item.qty_refunded >= item.qty_commanded) {
    statusCode = 'returned';
  } else if (item.qty_refunded && item.qty_refunded > 0) {
    statusCode = 'partially_returned';
  }
  // Check for shipped items
  else if (item.qty_shipped && item.qty_shipped >= item.qty_commanded) {
    statusCode = 'completed';
  } else if (item.qty_shipped && item.qty_shipped > 0) {
    statusCode = 'transit';
  }
  // Check for invoiced items
  else if (item.qty_invoiced && item.qty_invoiced > 0) {
    statusCode = 'en_cour_de_preparation';
  }
  // Default to processing
  else {
    statusCode = 'processing';
  }
  
  // Map status codes to badge configurations (using the same logic as in Orders.tsx)
  switch (statusCode.toLowerCase()) {
    case 'complete':
    case 'completed':
      return { type: 'success', icon: 'check-circle', label: t('orderStatus.completed'), code: statusCode };
    
    case 'processing':
      return { type: 'warning', icon: 'clock', label: t('orderStatus.processing'), code: statusCode };
    
    case 'pending':
      return { type: 'info', icon: 'clock', label: t('orderStatus.pending'), code: statusCode };
    
    case 'closed':
      return { type: 'secondary', icon: 'lock', label: t('orderStatus.closed'), code: statusCode };
    
    case 'canceled':
      return { type: 'danger', icon: 'x-circle', label: t('orderStatus.canceled'), code: statusCode };
    
    case 'returned':
      return { type: 'danger', icon: 'rotate-ccw', label: t('orderStatus.returned'), code: statusCode };
    
    case 'partially_returned':
      return { type: 'warning', icon: 'rotate-ccw', label: t('orderStatus.ready_to_return'), code: statusCode };
    
    case 'received':
      return { type: 'success', icon: 'check', label: t('orderStatus.received'), code: statusCode };
    
    case 'transit':
      return { type: 'info', icon: 'truck', label: t('orderStatus.in_transit'), code: statusCode };
    
    case 'en_cour_de_preparation':
      return { type: 'warning', icon: 'package', label: t('orderStatus.being_prepared'), code: statusCode };
    
    case 'prepared':
      return { type: 'success', icon: 'package', label: t('orderStatus.prepared'), code: statusCode };
    
    case 'en_cour_d_enlevement':
      return { type: 'info', icon: 'upload', label: t('orderStatus.in_transit'), code: statusCode };
    
    case 'receptionner_au_central':
      return { type: 'success', icon: 'home', label: t('orderStatus.receptionner_au_central'), code: statusCode };
    
    case 'enlever':
      return { type: 'info', icon: 'arrow-up', label: t('orderStatus.enlever'), code: statusCode };
    
    case 'transferer_au_central':
      return { type: 'info', icon: 'refresh-cw', label: t('orderStatus.transferer_au_central'), code: statusCode };
      
    default: {
      // For any other status, check if we have a translation for it
      const translationKey = `orderStatus.${statusCode.toLowerCase()}`;
      const translation = t(translationKey);
      
      // If translation key returns itself (no translation found), format the code
      return { 
        type: 'secondary', 
        icon: 'help-circle',
        label: translation !== translationKey ? translation : formatStatusLabel(statusCode),
        code: statusCode
      };
    }
  }
};

// Get color for item status (using the same Bootstrap color system as in OrderDetails.tsx)
const getItemStatusStyle = (statusType: string): { 
  bgColor: string, 
  textColor: string, 
  borderColor: string
} => {
  switch (statusType) {
    case 'success':
      return { 
        bgColor: 'bg-success-subtle', 
        textColor: 'text-success', 
        borderColor: 'border-success'
      };
    case 'warning':
      return { 
        bgColor: 'bg-warning-subtle', 
        textColor: 'text-warning', 
        borderColor: 'border-warning'
      };
    case 'danger':
      return { 
        bgColor: 'bg-danger-subtle', 
        textColor: 'text-danger', 
        borderColor: 'border-danger'
      };
    case 'info':
      return { 
        bgColor: 'bg-info-subtle', 
        textColor: 'text-info', 
        borderColor: 'border-info'
      };
    case 'primary':
      return { 
        bgColor: 'bg-primary-subtle', 
        textColor: 'text-primary', 
        borderColor: 'border-primary'
      };
    default:
      return { 
        bgColor: 'bg-secondary-subtle', 
        textColor: 'text-secondary', 
        borderColor: 'border-secondary'
      };
  }
};

// Define props for the component
interface OrderDetailsTableProps {
  orderItems?: OrderItem[];
}

const OrderDetailsTable = ({ orderItems = [] }: OrderDetailsTableProps) => {
  const { t } = useTranslation();
  
  // Determine status for each item and add it to the item
  const itemsWithStatus = useMemo(() => {
    return orderItems.map(item => ({
      ...item,
      statusConfig: getItemStatusConfig(item, t)
    }));
  }, [orderItems, t]);
  
  // Extract all unique attribute contents across all products to use as column headers
  const attributeContents = useMemo(() => {
    const contents = new Set<string>();
    
    itemsWithStatus.forEach(item => {
      if (item.configurable_attributes && item.configurable_attributes.length > 0) {
        item.configurable_attributes.forEach(attr => {
          contents.add(attr.content);
        });
      }
    });
    
    return Array.from(contents);
  }, [itemsWithStatus]);
  
  // Define all columns including dynamic attribute columns
  const columns = useMemo(() => {
    // Base columns that always appear first
    const baseColumns: ColumnDef<OrderItem>[] = [
      {
        accessorKey: 'name',
        header: t('orderDetailsTable.product'),
        cell: ({ row: { original } }) => {
          const statusConfig = getItemStatusConfig(original, t);
          const style = getItemStatusStyle(statusConfig.type);
          
          return (
            <div className="d-flex align-items-center gap-2">
              {/* Product Image with better fallback */}
              <div className={`rounded-2 border  d-flex align-items-center justify-content-center overflow-hidden`} 
                  style={{ width: '65px', height: '65px', flexShrink: 0 }}>
                {original.image ? (
                  <img 
                    src={original.image} 
                    alt={original.name} 
                    style={{ 
                      maxWidth: '100%', 
                      maxHeight: '100%', 
                      objectFit: 'contain',
                      padding: '2px'
                    }}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      // Add a text fallback when image fails to load
                      const parent = target.parentElement;
                      if (parent) {
                        parent.innerHTML = `
                          <div class="d-flex align-items-center justify-content-center text-center " style="width: 100%; height: 100%; padding: 4px; font-size: 0.65rem; font-weight: 600;">
                            ${original.name.substring(0, 20)}${original.name.length > 20 ? '...' : ''}
                          </div>
                        `;
                      }
                    }}
                  />
                ) : (
                  <div className={`d-flex align-items-center justify-content-center text-center ${style.textColor}`} 
                       style={{ width: '100%', height: '100%', padding: '4px', fontSize: '0.65rem', fontWeight: 600 }}>
                    {original.name.substring(0, 20)}{original.name.length > 20 ? '...' : ''}
                  </div>
                )}
              </div>
              
              {/* Product Name and SKU */}
              <div className="d-flex flex-column" style={{ minWidth: 0 }}>
                <Link 
                  to={`/products/${original.product_id}`} 
                  className="fw-semibold text-primary"
                  title={original.name}
                >
                  {original.name}
                </Link>
                <div className="fs--1 text-body-secondary mt-1 text-truncate">SKU: {original.sku}</div>
                <div className="fs--1 text-body-tertiary text-truncate">
                  Ref: {original.seller_reference || t('notAvailable')}
                </div>
              </div>
            </div>
          );
        },
        meta: {
          headerProps: { style: { minWidth: '350px', width: '38%' } },
          cellProps: { className: 'py-1 pe-3' }
        }
      }
    ];
    
    // Create dynamic attribute columns
    const attributeColumns = attributeContents.map(content => ({
      id: `attr_${content.toLowerCase().replace(/\s+/g, '_')}`,
      header: content,
      accessorFn: (row: OrderItem) => {
        if (!row.configurable_attributes) return '-';
        const attr = row.configurable_attributes.find(a => a.content === content);
        return attr ? attr.label : '-';
      },
      cell: ({ getValue }: { getValue: () => any }) => {
        const value = getValue();
        return <span>{value}</span>;
      },
      meta: {
        headerProps: { className: 'ps-3 pe-2' },
        cellProps: { className: 'ps-3 pe-2' }
      }
    }));
    
    // Common columns that appear after attribute columns
    const commonColumns: ColumnDef<OrderItem>[] = [
      {
        accessorKey: 'price',
        header: t('orderDetailsTable.price'),
        cell: ({ row: { original } }) => currencyFormat(original.price || 0),
        meta: {
          headerProps: { className: 'px-3 text-end' },
          cellProps: { className: 'text-body fw-semibold text-end px-3' }
        }
      },
      {
        id: 'combinedQuantities',
        header: t('orderDetailsTable.quantity'),
        cell: ({ row: { original } }) => {
          const statusConfig = getItemStatusConfig(original, t);
          const style = getItemStatusStyle(statusConfig.type);
          
          return (
            <div className="d-flex flex-column" style={{ fontSize: '0.7rem', lineHeight: '1.2' }}>
              <div className="d-flex justify-content-between align-items-center mb-1">
                <span className="me-1">{t('orderDetailsTable.ordered')}:</span>
                <span className="fw-bold">{original.qty_commanded || 0}</span>
              </div>
              {original.qty_invoiced > 0 && (
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <span className="me-1">{t('orderDetailsTable.invoiced')}:</span>
                  <span className="fw-bold">{original.qty_invoiced}</span>
                </div>
              )}
              {original.qty_shipped > 0 && (
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <span className="me-1">{t('orderDetailsTable.shipped')}:</span>
                  <span className="fw-bold">{original.qty_shipped}</span>
                </div>
              )}
              {original.qty_refunded > 0 && (
                <div className="d-flex justify-content-between align-items-center">
                  <span className="me-1">{t('orderDetailsTable.refunded')}:</span>
                  <span className="fw-bold">{original.qty_refunded}</span>
                </div>
              )}
            </div>
          );
        },
        meta: {
          headerProps: { className: 'text-center', style: { fontSize: '0.8rem', width: '100px' } },
          cellProps: { className: 'align-middle' }
        }
      },
      {
        id: 'commission',
        accessorKey: 'comission',
        header: t('orderDetailsTable.commission'),
        cell: ({ row: { original } }) => currencyFormat(original.comission || 0),
        meta: {
          headerProps: { className: 'px-3 text-end' },
          cellProps: { className: 'text-body-tertiary fw-semibold text-end px-3' }
        }
      },
      {
        id: 'netEarnings',
        accessorKey: 'actual_seller_amount',
        header: t('orderDetailsTable.netEarnings'),
        cell: ({ row: { original } }) => currencyFormat(original.actual_seller_amount || 0),
        meta: {
          headerProps: { className: 'px-3 text-end' },
          cellProps: { className: 'text-success fw-semibold text-end px-3' }
        }
      },
      {
        id: 'total',
        accessorKey: 'total_price',
        header: t('orderDetailsTable.total'),
        cell: ({ row: { original } }) => currencyFormat(original.total_price || 0),
        meta: {
          headerProps: { className: 'px-3 text-end' },
          cellProps: { className: 'fw-bold text-body-highlight text-end px-3' }
        }
      }
    ];
    
    return [...baseColumns, ...attributeColumns, ...commonColumns];
  }, [orderItems, attributeContents, t]);
  
  const table = useAdvanceTable({
    data: itemsWithStatus,
    columns,
    pageSize: 10,
    pagination: true,
    sortable: true
  });

  // Calculate order information and statistics
  const orderStats = useMemo(() => {
    let subtotal = 0;
    let totalItems = 0;
    let totalCommission = 0;
    let totalNetEarnings = 0;
    
    itemsWithStatus.forEach(item => {
      subtotal += (item.total_price || 0);
      totalItems += (item.qty_commanded || 0);
      totalCommission += (item.comission || 0);
      totalNetEarnings += (item.actual_seller_amount || 0);
    });
    
    return {
      subtotal,
      totalItems,
      totalCommission,
      totalNetEarnings
    };
  }, [itemsWithStatus]);

  if (itemsWithStatus.length === 0) {
    return (
      <div className="border rounded p-4 text-center text-body-secondary">
        {t('orderDetailsTable.noItemsFound')}
      </div>
    );
  }

  return (
    <div className="order-details-table-container">
      <AdvanceTableProvider {...table}>
        <div className="border-y border-translucent">
          {/* Apply more compact table styling */}
          <div style={{ overflowX: 'auto' }}>
            <AdvanceTable 
              tableProps={{ 
                className: 'phoenix-table table-sm mb-0',
                size: 'sm',
                style: { 
                  fontSize: '0.85rem',
                  tableLayout: 'auto',
                  width: '100%'
                }
              }}
              headerClassName="bg-light"
              rowClassName="border-bottom-0"
              /* Removed rowProps since we're using rowClassName instead */
            />
          </div>
          
          {/* Compact footer with essential stats */}
          <div className="d-flex justify-content-between align-items-center py-1 px-2 bg-light border-top">
            <div className="d-flex align-items-center gap-2">
                <small className="text-body-secondary fw-semibold">
                {t('orderDetailsTable.items')}: {orderStats.totalItems} | {t('orderDetailsTable.products')}: {itemsWithStatus.length}
              </small>
            </div>
            <div className="d-flex gap-3">
              <small className="text-body-secondary">
                {t('orderDetailsTable.commission').substring(0, 3)}: <span className="text-body-tertiary fw-semibold">{currencyFormat(orderStats.totalCommission)}</span>
              </small>
              <small className="text-body-secondary">
                {t('orderDetailsTable.netEarnings').substring(0, 3)}: <span className="text-success fw-semibold">{currencyFormat(orderStats.totalNetEarnings)}</span>
              </small>
              <small className="text-body-secondary">
                {t('orderDetailsTable.total')}: <span className="text-body-emphasis fw-bold">{currencyFormat(orderStats.subtotal)}</span>
              </small>
            </div>
          </div>
        </div>
      </AdvanceTableProvider>
    </div>
  );
};

export default OrderDetailsTable;
